/**
 * Relatórios Gerais Service - API calls for general reports
 */
import apiClient from './apiClient';

export interface RelatorioFilters {
  data_inicio: string;  // YYYY-MM-DD
  data_fim: string;     // YYYY-MM-DD
  hospital_ids?: number[];
  cliente_ids?: number[];
  auditor_ids?: number[];
  status?: string[];
  grupo?: string[];
  faixa_aging?: string[];
}

function buildParams(filters: RelatorioFilters): URLSearchParams {
  const params = new URLSearchParams();
  params.set('data_inicio', filters.data_inicio);
  params.set('data_fim', filters.data_fim);
  if (filters.hospital_ids?.length) params.set('hospital_ids', filters.hospital_ids.join(','));
  if (filters.cliente_ids?.length) params.set('cliente_ids', filters.cliente_ids.join(','));
  if (filters.auditor_ids?.length) params.set('auditor_ids', filters.auditor_ids.join(','));
  if (filters.status?.length) params.set('status', filters.status.join(','));
  if (filters.grupo?.length) params.set('grupo', filters.grupo.join(','));
  if (filters.faixa_aging?.length) params.set('faixa_aging', filters.faixa_aging.join(','));
  return params;
}

// JSON endpoints (all paginated)
export const getAgendamentos = async (filters: RelatorioFilters, page = 1, pageSize = 100) => {
  const params = buildParams(filters);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  const res = await apiClient.get(`/relatorios-gerais/agendamentos?${params}`);
  return res.data;
};

export const getConsolidadoQtde = async (filters: RelatorioFilters, page = 1, pageSize = 100) => {
  const params = buildParams(filters);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  const res = await apiClient.get(`/relatorios-gerais/consolidado-qtde?${params}`);
  return res.data;
};

export const getUltimas3Contagens = async (filters: RelatorioFilters, page = 1, pageSize = 100) => {
  const params = buildParams(filters);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  const res = await apiClient.get(`/relatorios-gerais/ultimas-3-contagens?${params}`);
  return res.data;
};

// N144: mesmo comparativo, aberto por produto+lote (traz lote e validade).
export const getUltimas3Detalhado = async (filters: RelatorioFilters, page = 1, pageSize = 100) => {
  const params = buildParams(filters);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  const res = await apiClient.get(`/relatorios-gerais/ultimas-3-detalhado?${params}`);
  return res.data;
};

export const getVencidosDetalhado = async (filters: RelatorioFilters, page = 1, pageSize = 100) => {
  const params = buildParams(filters);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  const res = await apiClient.get(`/relatorios-gerais/vencidos-detalhado?${params}`);
  return res.data;
};

export const getConsolidadoDetalhado = async (filters: RelatorioFilters, page = 1, pageSize = 100) => {
  const params = buildParams(filters);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  const res = await apiClient.get(`/relatorios-gerais/consolidado-detalhado?${params}`);
  return res.data;
};

export const getSegregacaoConsolidado = async (filters: RelatorioFilters, page = 1, pageSize = 100) => {
  const params = buildParams(filters);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  const res = await apiClient.get(`/relatorios-gerais/segregacao-consolidado?${params}`);
  return res.data;
};

// Excel download endpoints
async function downloadExcel(url: string, filters: RelatorioFilters, filename: string) {
  const params = buildParams(filters);
  const res = await apiClient.get(`${url}?${params}`, { responseType: 'blob' });
  const blob = new Blob([res.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

// #12: download de PDF (mesmo padrao blob, content-type pdf)
async function downloadPdf(url: string, filters: RelatorioFilters, filename: string) {
  const params = buildParams(filters);
  const res = await apiClient.get(`${url}?${params}`, { responseType: 'blob' });
  const blob = new Blob([res.data], { type: 'application/pdf' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export const downloadAgendamentosExcel = (filters: RelatorioFilters) =>
  downloadExcel('/relatorios-gerais/agendamentos/excel', filters, 'agendamentos.xlsx');

export const downloadConsolidadoQtdeExcel = (filters: RelatorioFilters) =>
  downloadExcel('/relatorios-gerais/consolidado-qtde/excel', filters, 'consolidado_qtde.xlsx');

// #12: Consolidado (qtde) em PDF
export const downloadConsolidadoQtdePdf = (filters: RelatorioFilters) =>
  downloadPdf('/relatorios-gerais/consolidado-qtde/pdf', filters, 'consolidado_qtde.pdf');

export const downloadUltimas3Excel = (filters: RelatorioFilters) =>
  downloadExcel('/relatorios-gerais/ultimas-3-contagens/excel', filters, 'ultimas_3_contagens.xlsx');

export const downloadUltimas3DetalhadoExcel = (filters: RelatorioFilters) =>
  downloadExcel('/relatorios-gerais/ultimas-3-detalhado/excel', filters, 'ultimas_3_detalhado.xlsx');

export const downloadVencidosExcel = (filters: RelatorioFilters) =>
  downloadExcel('/relatorios-gerais/vencidos-detalhado/excel', filters, 'vencidos_detalhado.xlsx');

export const downloadConsolidadoDetalhadoExcel = (filters: RelatorioFilters) =>
  downloadExcel('/relatorios-gerais/consolidado-detalhado/excel', filters, 'consolidado_detalhado.xlsx');

export const downloadSegregacaoConsolidadoExcel = (filters: RelatorioFilters) =>
  downloadExcel('/relatorios-gerais/segregacao-consolidado/excel', filters, 'segregacao_consolidado.xlsx');

export const getSolicitacaoColeta = async (filters: RelatorioFilters, page = 1, pageSize = 100) => {
  const params = buildParams(filters);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  const res = await apiClient.get(`/relatorios-gerais/solicitacao-coleta?${params}`);
  return res.data;
};

export const downloadSolicitacaoColetaExcel = (filters: RelatorioFilters) =>
  downloadExcel('/relatorios-gerais/solicitacao-coleta/excel', filters, 'coletas_consolidado.xlsx');

export const getConsolidadoLeitura = async (filters: RelatorioFilters, page = 1, pageSize = 100) => {
  const params = buildParams(filters);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  const res = await apiClient.get(`/relatorios-gerais/consolidado-leitura?${params}`);
  return res.data;
};

export const downloadConsolidadoLeituraExcel = (filters: RelatorioFilters) =>
  downloadExcel('/relatorios-gerais/consolidado-leitura/excel', filters, 'consolidado_leitura.xlsx');
